import { getAuth } from "firebase/auth";
import {
  LoaderFunctionArgs,
  redirect,
  ActionFunctionArgs,
} from "react-router-dom";
import {
  apiClient,
  ServiceRequestTableLoaderResult,
} from "~/services/apiClient";
import { FormattedServiceRequestData } from "~/types";
import { getAuthWrapper, INTENTS, loadCookiesData , getReadyAuthUser } from "~/utils";
import * as APITypes from "~/API";

export async function ServiceRequestTablePageLoader({
  request,
}: LoaderFunctionArgs): Promise<ServiceRequestTablePageLoaderType | Response> {
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) return redirect("/explore");
  const { ownerID, ownerType, role } = authAccessInfo.data;

  const isAdminOrSuperAdmin =
    role === APITypes.ManagerRolesType.SuperAdmin ||
    role === APITypes.ManagerRolesType.Admin;

  const result = await apiClient.loader<ServiceRequestTableLoaderResult>(
    "service-request-table",
    { ownerID, ownerType, userID: user.uid },
  );

  if (!result.success || !result.data) {
    console.error("Failed to load service requests:", result.error);
    return redirect("/explore");
  }

  const canCreateRequest =
    isAdminOrSuperAdmin ||
    result.data.serviceRequestLifecycleAccess?.createAccessStatus ===
      APITypes.ResourceAccessRightStatus.Granted;

  let filteredList: FormattedServiceRequestData[] = (result.data
    .serviceRequestList ?? []) as FormattedServiceRequestData[];

  if (!isAdminOrSuperAdmin) {
    filteredList = filteredList.filter((requestData) => {
      return (
        requestData.accessRights?.readAccessStatus ===
          APITypes.ResourceAccessRightStatus.Granted ||
        requestData.accessRights?.writeAccessStatus ===
          APITypes.ResourceAccessRightStatus.Granted
      );
    });
  }

  const subscriptionPlan = result.data.subscriptionPlan ?? {
    __typename: "SubscriptionPlan",
    id: "freetier-default",
    name: "FreeTier",
    description: "Free tier subscription plan",
    price: 0,
    isActive: true,
    features: [],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    _version: 1,
    _lastChangedAt: Math.floor(Date.now() / 1000),
  };
  const hasServiceRequestAccess =
    result.data.hasServiceRequestAccess ??
    subscriptionPlan.features.some(
      (feature) =>
        feature === APITypes.SubscriptionPlanFeature.RequestManagement,
    );

  return {
    filteredList,
    ownerID,
    ownerType,
    userName: user?.displayName || "",
    tenantList: result.data.tenantList ?? [],
    canCreateRequest,
    subscriptionPlan,
    hasServiceRequestAccess,
  };
}

export type ServiceRequestTablePageLoaderType = {
  filteredList: FormattedServiceRequestData[];
  tenantList: APITypes.Tenant[];
  userName: string;
  ownerID: string;
  ownerType: APITypes.OwnerType;
  canCreateRequest: boolean;
  subscriptionPlan: APITypes.SubscriptionPlan;
  hasServiceRequestAccess: boolean;
};

export async function ServiceRequestTablePageAction({
  request,
}: ActionFunctionArgs) {
  const formData = await request.formData();
  const intent = formData.get("intent");
  const auth = getAuth();
  const user = await getReadyAuthUser();
  if (!user) return redirect("/explore");
  const authAccessInfo = await loadCookiesData(user?.uid || "");
  if (!authAccessInfo.success) redirect("/explore");

  switch (intent) {
    case INTENTS._add_service_request: {
      const payload = JSON.parse(formData.get("body") as string);
      const result = await apiClient.sendIntent<APITypes.ServiceRequest>(
        INTENTS._add_service_request,
        {
          creatorID: user.uid,
          creatorName: user.displayName || user.email,
          ...payload,
        },
      );
      if (!result.success) return { success: false, error: result.error };
      return result;
    }
  }
  return null;
}
